/**
 * Client-side behavioural tracking + recommendation scoring.
 *
 * We record which products a visitor opens and how long they dwell on each
 * (time-on-product), persisted in localStorage. Recommendations are then
 * scored from that history against the catalogue - no backend, no auth, works
 * on the demo data immediately. The storage shape is deliberately simple so it
 * can later be mirrored to a `ProductView` table server-side.
 */
import { useEffect, useRef } from 'react';
import type { CatalogItem, TextSource } from './catalog';
import { garmentType, productColour } from './catalog';

const KEY = 'souqli.viewHistory.v1';
const MAX = 60;

export interface ViewRecord {
  id: string;
  title?: string;
  brand?: string;
  category?: string;
  garment: string;
  colour?: string;
  priceGBP: number;
  views: number;
  dwellMs: number;
  lastAt: number;
}

function read(): ViewRecord[] {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as ViewRecord[]) : [];
  } catch {
    return [];
  }
}

function write(records: ViewRecord[]) {
  try {
    localStorage.setItem(KEY, JSON.stringify(records.slice(0, MAX)));
  } catch {
    /* quota / privacy mode - ignore */
  }
}

export function getHistory(): ViewRecord[] {
  return read().sort((a, b) => b.lastAt - a.lastAt);
}

/** Record (or refresh) a product view. Bumps the view counter. */
export function recordView(p: TextSource) {
  const id = p?.id;
  if (!id) return;
  const records = read();
  const existing = records.find((r) => r.id === id);
  if (existing) {
    existing.views += 1;
    existing.lastAt = Date.now();
  } else {
    records.unshift({
      id,
      title: p.title,
      brand: p.brand,
      category: p.category,
      garment: garmentType(p),
      colour: productColour(p),
      priceGBP: p.priceGBP ?? 0,
      views: 1,
      dwellMs: 0,
      lastAt: Date.now(),
    });
  }
  write(records);
}

/** Add dwell time (ms) to a product the visitor was looking at. */
export function recordDwell(id: string, ms: number) {
  if (!id || ms <= 0) return;
  const records = read();
  const r = records.find((x) => x.id === id);
  if (!r) return;
  r.dwellMs += Math.min(ms, 10 * 60_000); // cap a single session at 10 min
  r.lastAt = Date.now();
  write(records);
}

export function clearHistory() {
  try { localStorage.removeItem(KEY); } catch { /* ignore */ }
}

/**
 * React hook for product detail pages. Records the view on mount and the dwell
 * time when the user leaves the page (unmount, tab hidden, or closes the tab).
 */
export function useProductView(p?: TextSource | null) {
  const startRef = useRef<number>(Date.now());
  const idRef = useRef<string | undefined>(p?.id);
  idRef.current = p?.id;

  useEffect(() => {
    if (!p?.id) return;
    recordView(p);
    startRef.current = Date.now();

    const flush = () => {
      const id = idRef.current;
      if (!id) return;
      recordDwell(id, Date.now() - startRef.current);
      startRef.current = Date.now();
    };
    const onVisibility = () => { if (document.visibilityState === 'hidden') flush(); };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('beforeunload', flush);
    return () => {
      flush();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('beforeunload', flush);
    };
    // re-run when the viewed product changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p?.id]);
}

/* ───────────────────── Recommendation scoring ───────────────────── */

const priceProximity = (a: number, b: number) => {
  const hi = Math.max(a, b, 1);
  return 1 - Math.min(1, Math.abs(a - b) / hi);
};

// Engagement weight: a long dwell or repeat views matter more than a glance.
const engagement = (r: ViewRecord) =>
  1 + Math.log1p(r.dwellMs / 1000) + (r.views - 1) * 0.5;

/**
 * Recommend catalogue items based on view history + dwell time.
 * Excludes already-viewed items so suggestions feel fresh; falls back to the
 * most-saved items when there's no history yet (cold start).
 */
export function getRecommendations<T extends CatalogItem>(catalogue: T[], limit = 5): T[] {
  const history = getHistory();
  const seen = new Set(history.map((h) => h.id));

  const popular = () =>
    [...catalogue].sort((a, b) => (b.saveCount ?? 0) - (a.saveCount ?? 0));

  if (history.length === 0) {
    return popular().slice(0, limit);
  }

  const scored = catalogue
    .filter((c) => !seen.has(c.id))
    .map((c) => {
      const cg = garmentType(c);
      const cc = productColour(c);
      let score = 0;
      for (const h of history) {
        const w = engagement(h);
        let sim = 0;
        if (h.category && c.category && h.category === c.category) sim += 3;
        if (h.brand && c.brand && h.brand === c.brand) sim += 2;
        if (h.garment === cg && cg !== 'other') sim += 2;
        if (h.colour && cc && h.colour === cc) sim += 1;
        sim += priceProximity(c.priceGBP ?? 0, h.priceGBP);
        score += w * sim;
      }
      return { c, score };
    })
    .sort((a, b) => b.score - a.score);

  const top = scored.slice(0, limit).map((s) => s.c);

  // Top up with popular items if the catalogue is small / sparse.
  if (top.length < limit) {
    const have = new Set(top.map((t) => t.id));
    for (const p of popular()) {
      if (have.has(p.id) || seen.has(p.id)) continue;
      top.push(p);
      if (top.length >= limit) break;
    }
  }
  return top;
}

export const hasViewHistory = () => getHistory().length > 0;
